import { useState, useRef, useEffect } from 'react';
import { TextField, Button, Tooltip, InputAdornment, CircularProgress, Typography } from '@mui/material';
import { Search, Storage } from '@mui/icons-material';

interface SearchBoxProps {
  onSearch: (value: string, isBackendSearch: boolean) => void;
  onClearSearch: () => void;
  loading?: boolean;
  hasActiveSearch?: boolean;
  searchInfo?: string;
  resultCount?: number;
  totalCount?: number;
  currentSearchTerm?: string;
}

export function SearchBox({
  onSearch,
  onClearSearch,
  loading = false,
  hasActiveSearch = false,
  searchInfo = '',
  resultCount,
  totalCount,
  currentSearchTerm = '',
}: SearchBoxProps) {
  // State for the current input value - initialize with currentSearchTerm
  const [inputValue, setInputValue] = useState(currentSearchTerm);

  // State for the current search term - initialize with currentSearchTerm
  const [searchTerm, setSearchTerm] = useState(currentSearchTerm);

  // State for backend search mode
  const [isBackendSearch, setIsBackendSearch] = useState(false);

  // Reference to the input field
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update local state when currentSearchTerm changes
  useEffect(() => {
    if (currentSearchTerm) {
      setInputValue(currentSearchTerm);
      setSearchTerm(currentSearchTerm);
    }
  }, [currentSearchTerm]);

  // Check if search button should be enabled
  const hasSearchText = inputValue.trim() !== '';

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue === '' && hasActiveSearch) {
      setTimeout(() => {
        clearSearch();
      }, 0);
    }
  };

  // Handle search execution
  const handleSearch = () => {
    if (hasSearchText) {
      const term = inputValue.trim();
      setSearchTerm(term);

      // Parent component will handle the loading state
      onSearch(term, isBackendSearch);
    }
  };

  // Handle database search using the stored search term
  const handleDatabaseSearch = () => {
    // Try to get search term from multiple sources in priority order
    const messageElement = document.querySelector('[data-searchterm]') as HTMLElement;
    const messageSearchTerm = messageElement?.dataset.searchterm;

    // Use terms in order of preference
    const term = messageSearchTerm || currentSearchTerm || searchTerm || inputValue.trim();

    if (term) {
      // Update local states
      setIsBackendSearch(true);
      setInputValue(term); // Keep input field showing the search term
      setSearchTerm(term); // Update stored search term

      // Parent component will handle loading state for database search
      onSearch(term, true);
    } else {
      console.warn('Attempted database search with empty search term');
    }
  };

  // Clear search
  const clearSearch = () => {
    setInputValue('');
    setSearchTerm('');
    setIsBackendSearch(false);
    onClearSearch();
  };

  // Button tooltip text
  const getButtonTooltip = () => {
    if (!hasSearchText) return 'Skriv inn søketekst først';
    if (loading) return 'Søker...';
    return 'Utfør søk';
  };

  // Show "no results" message when appropriate
  const showNoResultsMessage = hasActiveSearch && resultCount === 0 && !isBackendSearch;

  // Determine the current search term to display - prioritize parent prop
  const displaySearchTerm = currentSearchTerm || searchTerm || inputValue.trim() || '(tomt søk)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Search input and controls */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="søk..."
          variant="outlined"
          size="small"
          inputRef={searchInputRef}
          value={inputValue}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          style={{ flex: 1, minWidth: '200px' }}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hasSearchText) {
              e.preventDefault();
              handleSearch();
            }
          }}
        />

        <Tooltip title={getButtonTooltip()}>
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              onClick={handleSearch}
              disabled={!hasSearchText || loading}>
              {loading ? 'Søker...' : 'Søk'}
            </Button>
          </span>
        </Tooltip>

        {/* Always show database search button when there's an active client search */}
        {hasActiveSearch && !isBackendSearch && (
          <Tooltip title="Søk i hele databasen">
            <Button variant="outlined" color="primary" size="small" startIcon={<Storage />} onClick={handleDatabaseSearch} disabled={loading}>
              Søk i databasen
            </Button>
          </Tooltip>
        )}

        {hasActiveSearch && (
          <Button variant="outlined" size="small" onClick={clearSearch}>
            Fjern søk
          </Button>
        )}
      </div>

      {/* Show a message when no results found */}
      {showNoResultsMessage && (
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            marginTop: '8px',
          }}>
          <Typography data-searchterm={displaySearchTerm}>
            <b>Ingen resultater funnet for "{displaySearchTerm}" i gjeldende visning.</b> Forsøk å utvide søket eller naviger til andre sider.
          </Typography>
        </div>
      )}

      {/* Search result information with bold text */}
      {searchInfo && !showNoResultsMessage && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{fontWeight: 'bold'}}>
            {searchInfo}
            {resultCount !== undefined && totalCount !== undefined && (
              <span style={{ fontWeight: 'normal' }}>
                {' '}
                ({resultCount} av {totalCount} rader)
              </span>
            )}
          </Typography>
        </div>
      )}
    </div>
  );
}
